"use client"
import Image from 'next/image'
import Link from 'next/link'
import StarIcon from '@heroicons/react/24/solid/StarIcon'
import AddQuantity from "@/components/common/AddQuantity";
import useCartStore, { ICartItem } from '@/store/cartStore'
import useWishListStore, { IWishList } from '@/store/wishListStore'
import { toast } from '@/hooks/use-toast'
import useCommonStore from '@/store/commonStore'
import { useEffect, useState } from 'react'
import { GTMAddToWishlistHandler, GTMRemoveFromWishlistHandler } from '@/lib/analytics';

interface IObj {
  [key: string]: any
}

interface IProduct {
  pdt_nm: string
  price: string
  currency: string
  spl_prc: string
  spl_prc_in_num: number
  disc: string
  disc_typ: string
  slug: string
  thumbnail: string
  rating: number
  total_sold: number
  pdt_id: string
  path: string
  max_order_quantity: number
  variants?: IObj[]
  tags?: IObj[]
  in_stk: boolean
}

interface ProductCardProps {
  product: IProduct
  variant?: 'grid' | 'carousel'
  onAddToCart: (max_order_quantity: number, product: ICartItem | null, variants?: IObj[], type?: string, spl_prc?: number, pdt_nm?: string) => void
}

export function ProductCard({ product, variant = 'grid', onAddToCart }: ProductCardProps) {
  const { wishList, updateWishList } = useWishListStore()
  const { isAuth, translations } = useCommonStore()
  const [isInWishList, setIsInWishlist] = useState(false)

  const addToWishList = async (doc: IObj) => {
    let vid =""
    if(doc?.variants && doc?.variants.length>0){
      let inStockVariant= doc?.variants?.find((v: IObj) => v.in_stk)
      if(inStockVariant){
        vid=inStockVariant.variant_id
      }else{
        vid=doc?.variants[0]?.variant_id
      }
    }
    const reqObj: IWishList = {
      pdt_id: doc.pdt_id,
      variant_id: vid,
      flag: !isInWishList // toggle flag
    }    
    if (isInWishList){
          GTMRemoveFromWishlistHandler({
            items:[
              {
                item_id:doc.pdt_id,
                item_variant:doc?.variant_id ||"",
                price:doc?.spl_prc_in_num,
                item_name:doc?.pdt_nm,                
              }
            ]
          })
        }
        else{
          GTMAddToWishlistHandler({
            items:[
              {
                item_id:doc.pdt_id,
                item_variant:doc?.variant_id||"",
                price:doc?.spl_prc_in_num,
                item_name:doc?.pdt_nm, 
              }
            ]
          })
        }
    const result = await updateWishList(reqObj, isAuth)
    toast({
      variant: result.status ? "default" : "destructive",
      title: result.status ? "Success" : "Error",
      description: result.status ? result.message : result.error,
    })
  };

  useEffect(() => {
    setIsInWishlist(() => wishList.some(item => item.pdt_id === product.pdt_id) ? true : false)
  }, [wishList])
  const baseCardClass = variant === 'grid'
    ? 'rounded-lg relative w-full'
    : 'flex-shrink-0 w-[165px] md:w-[296px] rounded-lg'

  const imageClass = variant === 'grid'
    ? 'object-contain'
    : 'object-contain rounded-md m-auto h-[100%] md:h-[160px] p-3'

  const imageHeightClass = variant === 'grid' ? ' h-[225px] rounded-[14px]' : 'h-[225px] rounded-[8px]'
  const { cart } = useCartStore()

  return (
    <Link href={`/products/${product.slug}?pdt_id=${product.pdt_id}`} className={baseCardClass + ' relative'}>
      <div className="flex flex-col h-full gap-2 md:gap-3">
        <div className={'bg-chart-1 relative flex justify-center items-center border border-chart-2 p-2 ' + imageHeightClass}>
          {product?.tags?.map((tag: IObj, idx: number) => (
            <div className="absolute top-3 left-0 z-1 flex flex-col gap-1" key={idx}>
              <span style={{ backgroundColor: tag?.bg_clr || "#000", color: tag?.txt_clr || "#fff" }} className="text-xs font-semibold px-3 py-[3px] rounded-tr-md rounded-br-md">{tag?.tag_labl}</span>
            </div>
          ))}

          <button
            onClick={(e) => { e.preventDefault(); e.stopPropagation(); addToWishList(product) }}
            className={`absolute right-2 top-3 z-1  hover:text-red-600`}
          >
            <img src={`${process.env.NEXT_PUBLIC_ICON_URL}${isInWishList ? '17bd4ce5-874b-43ae-a87a-8a31f155362a.svg' : '9b871b36-dcdc-4f70-a107-7e9199484a38.svg'}`} alt="favourite" className='w-5 h-5' />
          </button>

          {(product.variants?.length ?? 0) > 0 && (
            <button
              onClick={(e) => {
                e.preventDefault()
                e.stopPropagation()
                onAddToCart(product?.max_order_quantity, null, product?.variants || [],"increment",product.spl_prc_in_num,product.pdt_nm)
              }}
              className="absolute text-xs font-bold border border-chart-2 px-[10px] py-[3px] rounded-md left-2 bottom-2 text-[#534637] bg-white z-5 shadow-[0_0_4px_0_#0000001F]"
            >
              {product.variants?.length ?? 0} {translations["variants"]||"Variants"}
            </button>
          )}

          <Image
            src={
              product?.thumbnail
                ? `${product.thumbnail}?h=110&w=110`
                : process.env.NEXT_PUBLIC_UNKNOWN_PRODUCT || ''
            }
            loading="eager"
  fetchPriority="high"
            alt={product.pdt_nm}
            width={160}
            height={160}
            className={`${imageClass} object-contain w-[140px] h-[140px] overflow-hidden`}
          />

          {product.rating > 0 && (
            <div className="absolute bottom-2 right-1 flex justify-between gap-1 bg-[linear-gradient(90deg,_#FFF8BF_0%,_#FFFFFF_96.49%)] rounded-[6px] px-1 py-[2px]">
              <div className=' flex items-center gap-1 text-xs text-black'>
                <div className="flex items-center gap-1">
                  <StarIcon className="w-3 h-3 text-yellow-500" />
                  <p className="font-bold text-yellow-500 text-xs">{product.rating}</p>
                </div>
                {product?.total_sold > 0 && (
                  <span className="text-xs text-gray-400 font-bold">({product.total_sold})</span>
                )}
              </div>
            </div>
          )}
        </div>
        <div>
        <h3 className="text-sm md:text-lg font-medium line-clamp-2 mb-1 md:mb-3">{product.pdt_nm}</h3>
        {(() => {
          const match = cart?.find(i => i.pdt_id === product?.pdt_id && !i.variant_id);
          const cartQnty = match?.quantity || 0;
          return (
            <>
              {product.disc && product.price != product.spl_prc && (
                <div className='flex items-center gap-2 mb-1 md:mb-3'>
                  <p className="text-xs font-bold text-[#01DB80]">{product.disc} {translations["off"] || "OFF"}</p>
                  <div className="border-dashed border-b flex-1"></div>
                </div>
              )}
              <div className='w-full flex justify-between items-center gap-2'>
                <div className="flex flex-col">
                  <span className="text-sm font-bold">{product.spl_prc}</span>
                  {product.price && product.price != product.spl_prc && (
                    <span className="text-xs text-[#808080] line-through font-bold">{product.price}</span>
                  )}
                </div>
                {product?.in_stk ? (
                  <AddQuantity
                    quantity={cartQnty}
                    onAdd={() => onAddToCart(product?.max_order_quantity, { pdt_id: product?.pdt_id, variant_id: match?.variant_id ?? "", quantity: cartQnty + 1 }, product?.variants || [],"increment",product.spl_prc_in_num,product.pdt_nm)}
                    onRemove={() => onAddToCart(product?.max_order_quantity, { pdt_id: product?.pdt_id, variant_id: match?.variant_id ?? "", quantity: cartQnty - 1 }, product?.variants || [],"decrease",product.spl_prc_in_num,product.pdt_nm)}
                  />
                ) : (
                  <span className="bg-red-200 p-2 py-1.5 rounded-lg text-sm font-bold text-red-700">{translations["out_of_stock"] || "Out of Stock"}</span>
                )}
              </div>
            </>
          )
        })()}
        </div>
      </div>
    </Link>
  )
}